import { AppModal } from '@euler/app/components/modal/AppModal';
import { Typography } from '@euler/app/components/typography';
import {
  AlertButtonContainer,
  AlertButtonProps,
  AlertContainer,
} from '@euler/app/tv/components/Alert';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePersistFn } from '@euler/utils/hooks';
import { Picker } from '@react-native-picker/picker';
import setMonth from 'date-fns/setMonth';
import setYear from 'date-fns/setYear';
import { range } from 'ramda';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  StyleProp,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  SharedValue,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Subject } from 'rxjs';
import {
  MaintenancePlanItem,
  MaintenancePlanStartParams,
  MaintenancePlanValueParams,
} from './types';
import {
  maintenancePlanDateByMileage,
  maintenancePlanMileageByDate,
} from './utils';

export type CustomMaintenancePlanParams = {
  item: MaintenancePlanItem;
  params: MaintenancePlanValueParams;
  confirmed$: Subject<MaintenancePlanValueParams | undefined>;
};

export const useCustomMaintenancePlanModal = () => {
  const [initialState, setInitialState] =
    useState<CustomMaintenancePlanParams>();
  const show = useCallback((value: CustomMaintenancePlanParams) => {
    setInitialState(value);
  }, []);
  const hide = useCallback(() => {
    setInitialState(undefined);
  }, []);
  return { show, hide, initialState };
};

export const CustomMaintenancePlanModal = memo(
  ({
    initialState,
    startParams,
    onClose,
    onConfirm,
  }: {
    initialState: CustomMaintenancePlanParams | undefined;
    startParams: MaintenancePlanStartParams;
    onClose?: () => void;
    onConfirm?: (value: CustomMaintenancePlanParams) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <AppModal
        visible={initialState != null}
        onDismiss={onClose}
        containerStyle={{ backgroundColor: theme.alert.backdropColor }}
        contentContainerStyle={{
          maxWidth: rpx(750),
          backgroundColor: theme.alert.bgColor,
        }}
      >
        {initialState != null && (
          <Content
            initialState={initialState}
            startParams={startParams}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        )}
      </AppModal>
    );
  },
);

const Content = memo(
  ({
    initialState,
    startParams,
    onClose,
    onConfirm,
  }: {
    initialState: CustomMaintenancePlanParams;
    startParams: MaintenancePlanStartParams;
    onClose?: () => void;
    onConfirm?: (value: CustomMaintenancePlanParams) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const currentIndex = useSharedValue(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mileage, setMileage] = useState(initialState.params.mileage);
    const [date, setDate] = useState(initialState.params.date);
    const [width, setWidth] = useState(0);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
      setWidth(e.nativeEvent.layout.width);
    }, []);

    const translateX = useDerivedValue(() =>
      interpolate(currentIndex.value, [0, 1], [0, -width]),
    );

    const tabContainerStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateX: translateX.value,
        },
      ],
    }));

    const onDone = usePersistFn(() => {
      const params: MaintenancePlanValueParams = { mileage, date };
      if (activeIndex === 0) {
        params.date = maintenancePlanDateByMileage(mileage, startParams);
      } else {
        params.mileage = maintenancePlanMileageByDate(date, startParams);
      }
      onConfirm?.({
        item: initialState.item,
        params,
        confirmed$: initialState.confirmed$,
      });
    });

    const buttons = useMemo<AlertButtonProps[]>(() => {
      return [
        {
          text: '返回',
          type: 'cancel',
          onPress: onClose,
        },
        {
          text: '确定保养',
          type: 'confirm',
          onPress: onDone,
        },
      ];
    }, [onClose, onDone]);

    return (
      <AlertContainer>
        <Tabs
          activeIndex={activeIndex}
          onChange={setActiveIndex}
          currentIndex={currentIndex}
        />
        <HStack
          justifyContent="flex-start"
          css={`
            overflow: hidden;
            align-self: stretch;
          `}
          onLayout={onLayout}
        >
          <Animated.View
            css={`
              flex-direction: row;
              justify-content: flex-start;
              align-items: stretch;
              height: ${rpx(300)}px;
              width: ${width * 2}px;
            `}
            style={tabContainerStyle}
          >
            <MileageTabContent
              value={mileage}
              onChange={setMileage}
              css={`
                width: ${width}px;
              `}
            />
            <TimeTabContent
              startDate={startParams.startDate}
              value={date}
              onChange={setDate}
              css={`
                width: ${width}px;
              `}
            />
          </Animated.View>
        </HStack>
        <AlertButtonContainer buttons={buttons} />
      </AlertContainer>
    );
  },
);

const MileageTabContent = memo(
  ({
    value,
    style,
    onChange,
  }: {
    value: number;
    style?: StyleProp<ViewStyle>;
    onChange: (value: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      task: {
        maintenancePlan: {
          custom: {
            textInputBgColor,
            textInputBorderColor,
            textInputTextColor,
            textInputUnitTextColor,
          },
        },
      },
    } = usePresentationTheme();

    const onTextChange = useCallback(
      (s: string) => {
        let val = Number(s.replace(/[^0-9]/g, ''));
        if (isNaN(val)) val = 0;
        onChange(val);
      },
      [onChange],
    );

    return (
      <View
        css={`
          padding: 0 ${rpx(44)}px;
          justify-content: center;
        `}
        style={style}
      >
        <View
          css={`
            height: ${rpx(108)}px;
            padding-right: ${rpx(90)}px;
            border-width: 1px;
            border-color: ${textInputBorderColor};
            background-color: ${textInputBgColor};
            border-radius: ${rpx(12)}px;
          `}
        >
          <TextInput
            value={String(value)}
            onChangeText={onTextChange}
            keyboardType="number-pad"
            css={`
              flex: 1;
              padding: 0 ${rpx(40)}px;
              margin: 0;
              font-family: ${FontFamily.NotoSans.Bold};
              font-weight: 700;
              font-size: ${rpx(32)}px;
              line-height: ${rpx(48)}px;
              color: ${textInputTextColor};
              text-align: center;
            `}
          />
          <View
            css={`
              position: absolute;
              right: ${rpx(30)}px;
              top: 0;
              bottom: 0;
              justify-content: center;
              align-items: flex-end;
            `}
          >
            <Typography.Label
              bold
              color={textInputUnitTextColor}
              size={rpx(32)}
              lineHeight={rpx(48)}
            >
              KM
            </Typography.Label>
          </View>
        </View>
      </View>
    );
  },
);

const TimeTabContent = memo(
  ({
    value,
    startDate,
    style,
    onChange,
  }: {
    value: Date;
    startDate: Date;
    style?: StyleProp<ViewStyle>;
    onChange: (value: Date) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      task: {
        maintenancePlan: {
          custom: { activePickerItemTextColor, pickerItemTextColor },
        },
      },
    } = usePresentationTheme();

    const onYearChange = useCallback(
      (year: number) => {
        onChange(setYear(value, year));
      },
      [onChange, value],
    );

    const onMonthChange = useCallback(
      (month: number) => {
        onChange(setMonth(value, month));
      },
      [onChange, value],
    );

    return (
      <HStack
        css={`
          padding: 0 ${rpx(44)}px;
        `}
        style={style}
      >
        <Picker
          selectedValue={value.getFullYear()}
          onValueChange={onYearChange}
          style={{
            flex: 3,
            justifyContent: 'center',
          }}
        >
          {range(startDate.getFullYear(), startDate.getFullYear() + 5).map(
            year => (
              <Picker.Item
                label={`${year}年`}
                value={year}
                key={year}
                style={{
                  fontFamily: FontFamily.NotoSans.Regular,
                  fontSize: rpx(32),
                  lineHeight: rpx(48),
                  color:
                    value.getFullYear() === year
                      ? activePickerItemTextColor
                      : pickerItemTextColor,
                }}
              />
            ),
          )}
        </Picker>
        <Picker
          selectedValue={value.getMonth()}
          onValueChange={onMonthChange}
          mode="dropdown"
          style={{
            flex: 2,
            justifyContent: 'center',
          }}
        >
          {range(0, 12).map(month => (
            <Picker.Item
              label={`${month + 1}月`}
              value={month}
              key={month}
              style={{
                fontFamily: FontFamily.NotoSans.Regular,
                fontSize: rpx(32),
                lineHeight: rpx(48),
                color:
                  value.getMonth() === month
                    ? activePickerItemTextColor
                    : pickerItemTextColor,
              }}
            />
          ))}
        </Picker>
      </HStack>
    );
  },
);

const Tabs = memo(
  ({
    activeIndex,
    currentIndex,
    onChange,
  }: {
    activeIndex: number;
    currentIndex: SharedValue<number>;
    onChange: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      task: {
        maintenancePlan: {
          custom: { tabUnderlineColor },
        },
      },
    } = usePresentationTheme();

    const timeTabPosX = useSharedValue(0);

    const underlineStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateX: interpolate(
            currentIndex.value,
            [0, 1],
            [0, timeTabPosX.value],
          ),
        },
      ],
    }));

    const onTimeTabLayout = useCallback(
      (e: LayoutChangeEvent) => {
        timeTabPosX.value = e.nativeEvent.layout.x;
      },
      [timeTabPosX],
    );

    const onTabPress = useCallback(
      (index: number) => {
        if (index === activeIndex) return;
        currentIndex.value = withTiming(
          index,
          {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
          },
          () => {
            runOnJS(onChange)(index);
          },
        );
      },
      [activeIndex, currentIndex, onChange],
    );

    return (
      <HStack>
        <Tab
          index={0}
          text="按预计保养里程"
          onPress={onTabPress}
          active={activeIndex === 0}
        />
        <Tab
          index={1}
          active={activeIndex === 1}
          text="按预计保养时间"
          onPress={onTabPress}
          onLayout={onTimeTabLayout}
        />
        <Animated.View
          css={`
            align-items: center;
            justify-content: center;
            width: 50%;
            position: absolute;
            left: 0;
            bottom: ${-rpx(16)}px;
          `}
          style={underlineStyle}
        >
          <View
            css={`
              width: ${rpx(80)}px;
              height: ${rpx(8)}px;
              border-radius: ${rpx(8) / 2}px;
              background-color: ${tabUnderlineColor}px;
            `}
          />
        </Animated.View>
      </HStack>
    );
  },
);

const Tab = memo(
  ({
    index,
    text,
    active,
    style,
    onPress,
    onLayout,
  }: {
    index: number;
    text: string;
    active?: boolean;
    onPress?: (index: number) => void;
    style?: StyleProp<ViewStyle>;
    onLayout?: (e: LayoutChangeEvent) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      task: {
        maintenancePlan: {
          custom: { tabTextColor, activeTabTextColor },
        },
      },
    } = usePresentationTheme();
    const handlePress = useCallback(() => {
      onPress?.(index);
    }, [index, onPress]);

    return (
      <TouchableOpacity
        css={`
          flex: 1;
          align-items: center;
          justify-content: center;
        `}
        style={style}
        onPress={handlePress}
        onLayout={onLayout}
      >
        <Text
          css={`
            font-family: ${active
              ? FontFamily.NotoSans.Bold
              : FontFamily.NotoSans.Regular};
            font-weight: ${active ? 700 : 400};
            font-size: ${rpx(32)}px;
            line-height: ${rpx(48)}px;
            color: ${active ? activeTabTextColor : tabTextColor};
          `}
        >
          {text}
        </Text>
      </TouchableOpacity>
    );
  },
);
